import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Mail, Lock, ArrowRight, Loader2, Check, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';

const features = [
  'Upload lecture slides & homework PDFs',
  'Get instant AI-generated practice exams',
  'Study with personalized flashcards & quizzes',
];

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup(email, password);

      if (result?.needsConfirmation) {
        setShowConfirmation(true);
        analytics.signupSuccess(email);
        toast.success('Check your email to confirm your account!');
      } else if (result?.session) {
        analytics.signupSuccess(email);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      let errorMsg = error?.message || 'Failed to create account';

      if (errorMsg.includes('body stream')) {
        errorMsg = 'Network error. Please try again.';
      } else if (errorMsg.includes('429') || errorMsg.toLowerCase().includes('too many')) {
        errorMsg = 'Too many attempts. Please wait a minute and try again.';
      } else if (errorMsg.toLowerCase().includes('already registered') || errorMsg.toLowerCase().includes('already been registered')) {
        errorMsg = 'This email is already registered. Please login instead.';
      }

      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (showConfirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <motion.div
          className="w-full max-w-md text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="border-2 border-border p-10">
            <div className="w-16 h-16 bg-primary mx-auto mb-6 flex items-center justify-center">
              <MailCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-black text-2xl mb-3">CHECK YOUR EMAIL</h1>
            <p className="text-muted-foreground mb-2">
              We sent a confirmation link to
            </p>
            <p className="font-semibold text-foreground mb-6">{email}</p>
            <p className="text-sm text-muted-foreground mb-8">
              Click the link in the email to activate your account, then come back to log in.
            </p>
            <Link to="/login">
              <Button className="w-full h-12 font-bold uppercase" data-testid="goto-login-after-confirm">
                Go to Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-4">
              Didn't receive the email? Check your spam folder.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-2/5 bg-primary flex-col justify-between p-10 text-white">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-10 h-10 bg-white/20 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-black text-xl tracking-tight">CALC AI</span>
        </Link>

        <div>
          <h2 className="font-heading font-black text-4xl leading-tight mb-8">
            Master Calculus With AI-Powered Study Tools
          </h2>
          <div className="space-y-4">
            {features.map((text) => (
              <div key={text} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-white/90 text-sm leading-relaxed">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/40 text-xs">© Powered by FHYAS LLC.</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile-only header */}
        <header className="lg:hidden border-b-2 border-border px-6 py-4">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="mb-8">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Get started free
              </span>
              <h1 className="font-heading font-black text-4xl tracking-tight mt-1">
                Create Account
              </h1>
              <p className="text-muted-foreground mt-2 text-sm">
                No credit card required · 5 free generations daily
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@university.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 border-2 rounded-none"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-mono text-xs uppercase tracking-wider">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 border-2 rounded-none"
                    required
                    data-testid="signup-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="font-mono text-xs uppercase tracking-wider">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12 border-2 rounded-none"
                    required
                    data-testid="signup-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-bold uppercase tracking-wide mt-2"
                disabled={isLoading}
                data-testid="signup-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t-2 border-border text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-primary font-semibold hover:underline"
                  data-testid="goto-login-link"
                >
                  Log in
                </Link>
              </p>
            </div>
          </motion.div>
        </main>

        {/* Mobile-only footer */}
        <footer className="lg:hidden border-t-2 border-border py-4 px-6 text-center">
          <p className="text-xs text-muted-foreground">© Powered by FHYAS LLC.</p>
        </footer>
      </div>
    </div>
  );
}
